import { Link } from "react-router-dom";
import natureImage1 from "../../assets/nature-1.png";
import SeriesNavigation from "../../components/SeriesNavigation";
import FadeInSection from "../../components/FadeInSection";
import { media } from "../../data/navigationData";
import SEO from "../../components/SEO";
import ScrollDepthTracker from "../../components/ScrollDepthTracker";

export default function NatureArticle() {
  const natureSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Nature Journal Breakthrough",
    "url": "https://www.thrombio.com.au/media/nature",
    "mainEntity": {
      "@type": "Organization",
      "name": "Thrombio",
      "url": "https://www.thrombio.com.au/",
      "logo": "https://www.thrombio.com.au/thromBioInvert.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      },
      "sameAs": [
        "https://www.linkedin.com/company/thrombioholdings"
      ]
    },
    "about": {
      "@type": "MedicalEntity",
      "name": "Thrombio Media",
      "description": "Thrombio's breakthrough research on blood clotting mechanism published in Nature."
    }
  };

  return (
    <>
      <SEO
        title="Nature Journal Publication"
        description="A landmark research study led by scientists in Sydney, in collaboration with researchers from New York, Melbourne and Brisbane, have discovered a new blood clotting mechanism."
        keywords={['Nature Journal', 'Research Publication', 'Blood Clotting Mechanism', 'COVID-19 Research', 'Microcirculation', 'Microvascular Obstruction', 'Haemolysis', 'Hemolysis', ' Necroptosis', 'Coronavirus', 'Hemostasis', 'Ischemia-reperfusion injury', 'Endothelial death', 'Cardiovascular disease Research', 'Red blood cell lysis', 'Ischemia', 'Terminal Complement Complex', 'Ischemic disease research', 'Blood clot', 'Anti-platelet', 'Platelet inhibitor']}
        ogImage="/nature-cover.png"
        schema={natureSchema}
      />
      <ScrollDepthTracker pageCategory="media" pageName="nature" />

      <div className="bg-primary">
        <div className="page-width px-6 md:px-20 pt-[18px] pb-8">
          <nav className="font-sans text-[1.8rem] font-normal leading-[2.3rem]" aria-label="Breadcrumb">
            <Link to="/media" className="text-white hover:text-primary-red transition-colors">
              Media
            </Link>
            <span className="mx-1 text-white"> | </span>
            <span className="text-[#5C95A4]">Nature</span>
          </nav>
        </div>

        <div className="page-width px-6 md:px-20 pt-24 pb-8">
          <FadeInSection>
            <img src="/Nature-logo-Red.png" alt="Nature Journal Logo" className="w-[310px]" />
          </FadeInSection>
        </div>

        <div className="page-width px-6 md:px-20 pb-16">
          <div className="flex flex-col md:flex-row gap-20 mb-6">
            <div className="md:w-1/2 lg:w-3/5 order-2 md:order-1">
              <FadeInSection className="delay-500">
                <p className="text-white font-sans text-[2.4rem] leading-[3.8rem] tracking-wide">
                  A landmark research study led by scientists in Sydney, in collaboration with researchers from New York, Melbourne and Brisbane, have discovered a new blood clotting mechanism that wreaks havoc on the small blood vessels (microcirculation) of major organs. Disrupting the flow of blood to vital organs deprives them of oxygen and nutrients, leading to ischemic organ injury. This pathological process is common and is the main cause of organ injury in people suffering from a heart attack, stroke or septicaemic shock. Unexpectantly, Sydney scientists have shown that this ischaemic process is also rampant in severely ill COVID-19 patients, leading to widespread blockages in the microcirculation, causing severe organ injury.
                </p>
              </FadeInSection>
            </div>

            <div className="md:w-1/2 lg:w-2/5 flex items-start justify-end order-1 md:order-2">
              <FadeInSection className="delay-500">
                <img
                  src={natureImage1}
                  alt="Nature Journal Cover featuring COVID Questions"
                  className="max-w-full md:max-w-[90%] h-auto md:h-[510px] object-contain"
                />
              </FadeInSection>
            </div>
          </div>

          <div className="mb-16">
            <FadeInSection>
              <div className="flex justify-center">
                <img
                  src="/Earth Human Capillaries.png"
                  alt="Earth with human circulatory system visualization showing global scale of capillaries"
                  className="w-[747px] h-auto"
                />
              </div>
            </FadeInSection>

            <FadeInSection>
              <div className="mb-24">
                <h2 className="text-[#C3EEFD] text-[2.3rem] leading-[2.9rem] font-bold italic mb-1">
                  The Enormity of the Human Microcirculatory System
                </h2>
                <p className="text-[#C3EEFD] text-[2.2rem] leading-[2.9rem] font-light italic">
                  In the human body, there are literally billions of small blood vessels (capillaries) that are not visible to the naked eye. These critical parts of the microcirculatory system provide essential oxygen and nutrients to all organs and tissues. If all capillaries from one human were connected end-to-end they would circumnavigate the Earth (~40,000km).
                </p>
              </div>
            </FadeInSection>
          </div>

          <div className="mb-16">
            <FadeInSection>
              <p className="text-white font-sans text-[2.4rem] leading-[3.8rem] tracking-wide">
                Published in the prestigious journal Nature, the study reveals that red blood cells – the body's oxygen carriers – are surprisingly turning against us. Under conditions of low blood flow, such as during COVID-19, stroke or heart attacks, the inner lining of blood vessels (endothelial cells) begins to die through a process known as necroptosis. This triggers nearby red blood cells to rupture, releasing sticky remnants that clog up the smallest blood vessels and cut off oxygen to crucial tissues.
              </p>
            </FadeInSection>
          </div>

          <div className="mb-24">
            <FadeInSection>
              <div className="mb-8">
                <img
                  src="/Schematic Red Cell Occlusion Nature 1.png"
                  alt="Scientific diagram showing blood vessel process with healthy and damaged capillaries"
                  className="w-full h-auto"
                />
              </div>
            </FadeInSection>

            <FadeInSection>
              <h2 className="text-[#C3EEFD] text-[2.3rem] leading-[2.9rem] font-bold italic mb-1">
                Discovery of a New Process Preventing Microcirculatory Bleeding and Causing Capillary Obstruction
              </h2>
              <div>
                <p className="text-[#C3EEFD] text-[2.2rem] leading-[2.9rem] font-light italic">
                  <span className="font-light">(A)</span> In healthy capillaries, red blood cells provide oxygen to the tissues and remove carbon dioxide.
                </p>
                <p className="text-[#C3EEFD] text-[2.2rem] leading-[2.9rem] font-light italic">
                  <span className="font-light">(B)</span> When blood flow is impaired, the endothelial lining of blood vessels can become damaged and die. This then leads to red cells escaping into the tissue, causing bleeding.
                </p>
                <p className="text-[#C3EEFD] text-[2.2rem] leading-[2.9rem] font-light italic">
                  <span className="font-light">(C)</span> To prevent bleeding, red cells fragment and form a sealant at sites of endothelial death. Excessive endoseal formation leads to blockages in the injured capillaries.
                </p>
              </div>
            </FadeInSection>
          </div>

          <div className="flex flex-col gap-8">
            <FadeInSection>
              <p className="text-white font-sans text-[2.4rem] leading-[3.8rem] tracking-wide">
                The implications are profound. This new form of microvascular blockage was not only widespread in COVID-19 patients – where traditional blood thinners have shown limited effect – but also found in people who died from heart attacks, strokes and gut ischemia. In mouse models, blocking the key necroptosis pathway dramatically reduced both red cell damage and organ injury.
              </p>
            </FadeInSection>

            <FadeInSection>
              <p className="text-white font-sans text-[2.4rem] leading-[3.8rem] tracking-wide">
                "This mechanism helps explain why many patients with severe COVID-19 or other critical illnesses suffer from multi-organ failure, even when clotting is under control," said Professor Jackson. It may also explain some of the chronic symptoms of Long-COVID-19. "It's a whole new chapter in vascular biology and has major implications for common human diseases, such as heart attack and stroke."
              </p>
            </FadeInSection>

            <FadeInSection>
              <p className="text-white font-sans text-[2.4rem] leading-[3.8rem] tracking-wide">
                Crucially, this discovery opens the door to new treatment strategies. The Sydney scientists are at the forefront of developing innovating new treatments for stroke and are currently performing Phase II clinical trials on a new anticlotting drug that helps dissolve blood clots and prevent brain injury. Mr Peter Bush (CEO, ThromBio) states "the holy grail in stroke therapy is to rapidly dissolve blood clots and restore normal blood flow to the brain, without causing excess bleeding". The scientific team lead by Prof. Shaun Jackson has been at the forefront of developing new, innovative anticlotting approaches which may have a major impact on many ischaemic diseases, including stroke, heart attacks, septicemia, and now, potentially COVID-19.
              </p>
            </FadeInSection>

            <FadeInSection>
              <div>
                <p className="text-white font-sans text-[2.4rem] leading-[3.8rem] tracking-wide">
                  To learn more about ThromBio's breakthrough clinical trials,
                  <span> </span>
                  <Link to="/media/abc" className="underline hover:text-[#C3EEFD]">
                    click here
                  </Link>
                </p>
              </div>
            </FadeInSection>
          </div>
        </div>
        <SeriesNavigation
          items={media}
          currentSlug="nature"
          basePath="/media"
        />
      </div>
    </>
  );
}